import { AfterViewInit, Component, Input, OnInit, signal, ChangeDetectionStrategy, inject, input, viewChild } from '@angular/core';
import { PermissionGroup, PermissionGroupService, PermissionGroupUser } from '@eadsysteme/ewsauthclient-v1';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '../../../../services/snackbar.service';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../../confirmation-dialog/confirmation-dialog.component';
import { InviteUserDialogComponent } from './dialog/create-user-profile/invite-user-dialog.component';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
	MatTableDataSource,
} from '@angular/material/table';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { Icons } from '../../../../util/Icons';
import { MatTooltip } from '@angular/material/tooltip';
import { ChangeGroupNameDialogComponent } from './dialog/change-group-name/change-group-name-dialog.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { fadeInUp400ms } from '../../../../../@vex/animations/fade-in-up.animation';

export interface TableUser extends PermissionGroupUser {
	role: 'MEMBER' | 'ADMIN' | 'SUPER_ADMIN';
}

@Component({
	selector: 'ara-pm-permission-group',
	imports: [
		TranslocoDirective,
		MatIcon,
		MatIconButton,
		MatButton,
		MatTable,
		MatSort,
		MatColumnDef,
		MatHeaderCell,
		MatHeaderCellDef,
		MatCell,
		MatCellDef,
		MatMenu,
		MatMenuItem,
		MatHeaderRow,
		MatHeaderRowDef,
		MatRowDef,
		MatRow,
		MatSortHeader,
		MatTooltip,
		MatMenuTrigger,
		MatProgressSpinner,
	],
	animations: [fadeInUp400ms],
	templateUrl: './permission-group.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './permission-group.component.scss',
})
export class PermissionGroupComponent implements OnInit, AfterViewInit {
	dialog = inject(MatDialog);
	groupService = inject(PermissionGroupService);
	snackbar = inject(SnackbarService);
	transloco = inject(TranslocoService);
	icons = inject(Icons);

	@Input()
	public permissionGroup: PermissionGroup;
	public readonly isDevRole = input<boolean>(undefined);
	public readonly keycloakId = input<string>(undefined);
	public readonly showGroupName = input<boolean>(undefined);

	readonly sort = viewChild(MatSort);

	displayedColumns: string[] = ['name', 'email', 'role', 'actions'];
	dataSource = new MatTableDataSource<TableUser>();

	isSuperAdmin = false;
	isAdmin = false;
	isMember = false;
	allowSuperAdminDeletion = signal(false);
	isLoading = signal<boolean>(false);

	ngOnInit() {
		this.isSuperAdmin = this.permissionGroup.superAdmins.some((user) => user.id === this.keycloakId());
		this.isAdmin = this.permissionGroup.admins.some((user) => user.id === this.keycloakId());
		this.isMember = this.permissionGroup.members.some((user) => user.id === this.keycloakId());

		this.refreshData();
	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort();
	}

	checkSuperAdminDeletion(): void {
		this.allowSuperAdminDeletion.set(this.permissionGroup.superAdmins.length > 1 && this.isSuperAdmin);
	}

	renameGroup() {
		this.openChangeGroupNameDialog();
	}

	private refreshData(): void {
		const superAdmins: TableUser[] = this.permissionGroup.superAdmins.map((u) => ({ ...u, role: 'SUPER_ADMIN' }));
		const admins: TableUser[] = this.permissionGroup.admins.map((u) => ({ ...u, role: 'ADMIN' }));
		const members: TableUser[] = this.permissionGroup.members.map((u) => ({ ...u, role: 'MEMBER' }));

		this.dataSource.data = [...superAdmins, ...admins, ...members];

		// Custom sorting for the 'role' column
		this.dataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'name':
					return item.lastName.toLowerCase();
				case 'role':
					return this.getRoleSortOrder(item.role);
				default:
					return (item as any)[property];
			}
		};
		this.checkSuperAdminDeletion();
	}

	private getRoleSortOrder(role: 'MEMBER' | 'ADMIN' | 'SUPER_ADMIN'): number {
		switch (role) {
			case 'SUPER_ADMIN':
				return 2;
			case 'ADMIN':
				return 1;
			case 'MEMBER':
				return 0;
			default:
				return -1;
		}
	}

	openInviteUserDialog(): void {
		// Determine which roles the current user is allowed to assign
		let availableRoles: ('MEMBER' | 'ADMIN' | 'SUPER_ADMIN')[] = [];
		if (this.isSuperAdmin) {
			availableRoles = ['SUPER_ADMIN', 'ADMIN', 'MEMBER'];
		} else if (this.isAdmin) {
			availableRoles = ['ADMIN', 'MEMBER'];
		}

		const dialogRef = this.dialog.open(InviteUserDialogComponent, {
			width: '450px',
			data: {
				groupName: this.permissionGroup.groupName,
				availableRoles: availableRoles.sort((a, b) => this.getRoleSortOrder(a) - this.getRoleSortOrder(b)),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(
				filter((result) => !!result),
				switchMap((result) => {
					this.isLoading.set(true);
					return this.groupService
						.addUserToPermissionGroup(this.permissionGroup.groupId, result.role, result.profile)
						.pipe(finalize(() => this.isLoading.set(false)));
				}),
				tap((updatedGroup) => {
					this.snackbar.success(this.transloco.translate('permissionStages.addedUser'));
					this.permissionGroup = updatedGroup;
					this.refreshData();
				})
			)
			.subscribe({
				error: (err) => {
					if (err?.status === 409) {
						this.snackbar.error(this.transloco.translate('permissionStages.userWithThisEmailAlreadyExists'));
					} else {
						this.snackbar.error(this.transloco.translate('permissionStages.addUserFailed'));
					}
				},
			});
	}

	openChangeGroupNameDialog() {
		const dialogRef = this.dialog.open(ChangeGroupNameDialogComponent, {
			width: '450px',
			data: {
				groupName: this.permissionGroup.groupName,
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result && result.changedGroupName !== this.permissionGroup.groupName) {
				this.groupService.renamePermissionGroup(this.permissionGroup.groupId, result.changedGroupName).subscribe({
					next: (updatedGroup) => {
						this.snackbar.success(this.transloco.translate('permissionStages.changedGroupName'));
						this.permissionGroup = updatedGroup;
						this.refreshData();
					},
					error: (err) => {
						if (err?.status === 409) {
							this.snackbar.error(this.transloco.translate('permissionStages.duplicateGroupName'));
						} else if (err?.status === 403) {
							this.snackbar.error(this.transloco.translate('common.insufficientPermissions'));
						} else {
							this.snackbar.error(this.transloco.translate('permissionStages.changeGroupNameError'));
						}
					},
				});
			}
		});
	}

	changeUserRole(user: TableUser, newRole: 'MEMBER' | 'ADMIN' | 'SUPER_ADMIN'): void {
		if (user.role === newRole) return;

		this.isLoading.set(true);
		this.groupService
			.updateUserRoleInPermissionGroup(this.permissionGroup.groupId, newRole, user.id)
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe({
				next: (updatedGroup) => {
					this.permissionGroup = updatedGroup;
					this.refreshData();
					this.snackbar.success(this.transloco.translate('permissionStages.roleChanged'));
				},
				error: (err) => {
					if (err.status === 403) {
						this.snackbar.error(this.transloco.translate('common.insufficientPermissions'));
					} else {
						this.snackbar.error(this.transloco.translate('permissionStages.roleChangeFailed'));
					}
				},
			});
	}

	removeUserFromGroup(user: TableUser) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent);
		dialogRef.componentInstance.titleMessage = this.transloco.translate('permissionStages.deleteUser');
		dialogRef.componentInstance.confirmMessage = this.transloco.translate('permissionStages.deleteUserConfirm', {
			userName: user.lastName + ' ' + user.firstName,
		});
		dialogRef.afterClosed().subscribe((confirmed) => {
			if (confirmed) {
				this.isLoading.set(true);
				this.groupService
					.removeUserFromPermissionGroup(this.permissionGroup.groupId, user.role, user.id)
					.pipe(finalize(() => this.isLoading.set(false)))
					.subscribe({
						next: (updatedGroup) => {
							this.snackbar.success(this.transloco.translate('permissionStages.deletedUser'));
							this.permissionGroup = updatedGroup;
							this.refreshData();
						},
						error: (err) => {
							if (err.status === 403) {
								this.snackbar.error(this.transloco.translate('common.insufficientPermissions'));
							} else {
								this.snackbar.error(this.transloco.translate('permissionStages.deleteUserFailed'));
							}
						},
					});
			}
		});
	}
}
